/**
 * Pack Découverte — Cart Bundle Manager
 * ============================================================
 * Regroupe visuellement les articles pack dans le panier Shopify
 * à la façon Sapiens : header "Pack Corps", articles listés en
 * sous-items, contrôles de quantité masqués, bouton "Retirer".
 *
 * Installation :
 *   Ajoutez {% render 'packdecouverteverlou-cart' %} dans :
 *   • sections/main-cart-items.liquid   (page panier — Dawn / thèmes modernes)
 *   • sections/cart-template.liquid     (anciens thèmes)
 *   • sections/cart-drawer.liquid       (tiroir panier, si séparé)
 * ============================================================
 */

(function () {
  'use strict';

  /* ── Constantes ─────────────────────────────────────────── */

  const PACK_PROP      = '_packdecouverteverlou';
  const PACK_TYPE_PROP = '_pack_type';

  const PACK_LABELS = {
    corps:   'Pack Corps',
    visage:  'Pack Visage',
    complet: 'Pack Complet',
    unite:   "Sélection à l'unité",
  };

  /* ── Classe principale ──────────────────────────────────── */

  class PackDecouverteCartManager {
    constructor() {
      this._pendingRender = false;
      this._mutationTimer = null;
      this._observer      = null;

      this._init();
    }

    /* ── Init ─────────────────────────────────────────────── */

    _init() {
      this._scheduleRender();

      // Ré-exécuter après mise à jour AJAX du panier (thèmes qui dispatche ces events)
      // Palo Alto (Fluorescent) utilise les events préfixés 'on:'
      [
        'cart:updated',
        'cart:refresh',
        'cart:change',
        'on:cart:change',   // Palo Alto
        'on:cart:update',   // Palo Alto
        'on:cart:open',     // Palo Alto — drawer ouvert = potentiel nouveau rendu
        'shopify:section:load',
      ].forEach((ev) =>
        document.addEventListener(ev, () => this._scheduleRender())
      );

      // Observer DOM : couvre les cart drawers qui reconstruisent le HTML
      // sans dispatche d'événements (ex. Impulse, Prestige, certains sous-thèmes)
      this._observeCartDrawer();

      // Forcer le passage par /cart avant /checkout (comportement drawer)
      this._interceptCheckout();
    }

    _scheduleRender() {
      if (this._pendingRender) return;
      this._pendingRender = true;
      // Léger délai pour laisser le thème finir son rendu DOM
      setTimeout(async () => {
        try {
          await this._render();
        } catch (err) {
          console.warn('[pd-cart]', err);
        } finally {
          this._pendingRender = false;
        }
      }, 80);
    }

    _observeCartDrawer() {
      // On observe les changements dans .body mais on cible uniquement les
      // noeuds qui ressemblent à du contenu panier pour éviter de retrigger
      // inutilement (ex. animations, tooltips).
      this._observer = new MutationObserver((mutations) => {
        for (const mut of mutations) {
          for (const node of mut.addedNodes) {
            if (node.nodeType !== 1) continue;
            if (
              node.querySelector?.('[data-key]') ||
              node.querySelector?.('[name*="updates"]') ||
              node.querySelector?.('[data-js="cart-item"]') ||  // Palo Alto
              node.classList?.contains('cart-item') ||
              node.classList?.contains('cart__items') ||  // Palo Alto
              node.className?.includes?.('cart')
            ) {
              clearTimeout(this._mutationTimer);
              this._mutationTimer = setTimeout(
                () => this._scheduleRender(),
                120
              );
              return;
            }
          }
        }
      });
      this._observer.observe(document.body, { childList: true, subtree: true });
    }

    /* ── Interception checkout → /cart ───────────────────── */

    _interceptCheckout() {
      // Quand on est déjà sur /cart, on laisse le checkout se faire normalement.
      // Sinon (drawer…), on redirige vers /cart d'abord.
      const goToCart = (e) => {
        if (window.location.pathname === '/cart') return;
        e.preventDefault();
        e.stopImmediatePropagation();
        window.location.href = '/cart';
      };

      // 1. Interception du submit du formulaire panier
      document.addEventListener('submit', (e) => {
        if (window.location.pathname === '/cart') return;
        if (!e.target.querySelector('button[name="checkout"], [data-cart-checkout]')) return;
        goToCart(e);
      }, true);

      // 2. Interception du clic (Palo Alto peut soumettre via JS)
      document.addEventListener('click', (e) => {
        if (window.location.pathname === '/cart') return;
        const btn = e.target.closest(
          'button[name="checkout"], [data-cart-checkout], .cart__checkout'
        );
        if (!btn) return;
        goToCart(e);
      }, true);
    }

    /* ── Rendu principal ──────────────────────────────────── */

    async _render() {
      // Nettoyage des classes du rendu précédent
      document
        .querySelectorAll('.pd-cart-item--locked')
        .forEach((el) => el.classList.remove('pd-cart-item--locked'));

      // Données panier fraîches
      const cart = await this._fetchCart();
      if (!Array.isArray(cart?.items)) return;

      // Grouper les articles pack par type
      const packGroups = {};
      cart.items.forEach((item) => {
        if (!item.properties) return;
        if (item.properties[PACK_PROP] !== 'true') return;
        const packType = item.properties[PACK_TYPE_PROP] || 'pack';
        if (!packGroups[packType]) packGroups[packType] = { items: [], label: null };
        // Utiliser la propriété visible "Pack" comme label si disponible
        packGroups[packType].label = packGroups[packType].label
          || item.properties['Pack']
          || null;
        packGroups[packType].items.push(item);
      });

      if (!Object.keys(packGroups).length) return;

      Object.entries(packGroups).forEach(([packType, { items, label }]) => {
        this._decoratePackGroup(packType, items, label);
      });
    }

    async _fetchCart() {
      const resp = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!resp.ok) throw new Error(`cart.js ${resp.status}`);
      return resp.json();
    }

    /* ── Trouver un élément DOM pour un line item ─────────── */

    _findItemEl(item) {
      // 1. data-key (Dawn, Impulse, Symmetry, Expanse…)
      let el = document.querySelector(`[data-key="${item.key}"]`);
      if (el) return el;

      // 2. Palo Alto (Fluorescent) : data-item="{{ line_item.key }}"
      el = document.querySelector(`[data-item="${item.key}"]`);
      if (el) return el;

      // 3. Variantes du nom d'attribut
      el = document.querySelector(
        `[data-line-item-key="${item.key}"], [data-item-key="${item.key}"]`
      );
      if (el) return el;

      // 4. Palo Alto : data-item-index (index 1-based, fallback si key introuvable)
      if (typeof item.index === 'number') {
        const byIndex = document.querySelector(
          `[data-item-index="${item.index}"], [data-line-index="${item.index}"]`
        );
        if (byIndex) return byIndex;
      }

      // 5. Via l'input quantité (universel)
      //    La plupart des thèmes rendent : <input name="updates[KEY]">
      //    Palo Alto : <input name="updates[]" data-id="KEY"> (sans le key dans le name)
      const inputById = document.querySelector(
        `input[data-id="${item.key}"]`
      );
      if (inputById) {
        let node = inputById;
        while (node && node !== document.body) {
          if (
            node.tagName === 'TR' || node.tagName === 'LI' ||
            /cart[-_]?item|cart[-_]?product/i.test(node.className)
          ) return node;
          node = node.parentElement;
        }
      }

      const input = document.querySelector(`input[name="updates[${item.key}]"]`);
      if (input) {
        let node = input;
        while (node && node !== document.body) {
          if (
            node.tagName === 'TR' || node.tagName === 'LI' ||
            /cart[-_]?item|cart[-_]?product/i.test(node.className)
          ) return node;
          node = node.parentElement;
        }
      }

      // 6. Palo Alto fallback : chercher par variant_id
      if (item.variant_id) {
        const byVariant = document.querySelector(`[data-variant-id="${item.variant_id}"]`);
        if (byVariant && /cart[-_]?item|line[-_]?item/i.test(byVariant.className)) {
          return byVariant;
        }
      }

      return null;
    }

    /* ── Décorer un groupe de pack ────────────────────────── */

    _decoratePackGroup(packType, items, _customLabel) {
      const found = items
        .map((item) => ({ item, el: this._findItemEl(item) }))
        .filter(({ el }) => el !== null);

      if (!found.length) return;

      // Verrouiller chaque item (CSS masque qty + remove)
      found.forEach(({ el }) => el.classList.add('pd-cart-item--locked'));
    }

    /* ── Mise à jour du compteur panier du thème ──────────── */

    _refreshCartCount() {
      fetch('/cart.js', { credentials: 'same-origin' })
        .then((r) => r.json())
        .then((cart) => {
          const count = cart?.item_count ?? 0;

          // Dawn / thèmes modernes : data-cart-count
          // Palo Alto : [data-js="cart-count"], .cart-count-bubble span
          document
            .querySelectorAll(
              '[data-cart-count], .cart-count, .header__cart-count, ' +
              '[data-js="cart-count"], .cart-count-bubble, .cart__count'
            )
            .forEach((el) => { el.textContent = count; });

          // Shopify custom event standard
          document.dispatchEvent(
            new CustomEvent('cart:count-updated', { detail: { count } })
          );

          // Palo Alto — event préfixé 'on:'
          document.dispatchEvent(
            new CustomEvent('on:cart:count-updated', { detail: { count, cart } })
          );
        })
        .catch(() => {});
    }
  }

  /* ── Démarrage (singleton) ───────────────────────────────── */
  // Guard : si le snippet est inclus dans plusieurs fichiers Liquid
  // (page panier + drawer), le script est exécuté deux fois.
  // On crée une seule instance pour éviter les doublons d'observers.

  function init() {
    if (window._pdCartManagerActive) return;
    window._pdCartManagerActive = true;
    new PackDecouverteCartManager();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();